import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type UseMutationResult,
} from '@tanstack/react-query'
import agentAuthService from './agent-auth.service'
import type {
  AgentLoginByEmailInput,
  AgentLoginByPhoneInput,
  AgentRegisterByEmailInput,
  AgentRegisterByPhoneInput,
  AgentRefreshTokenInput,
  AgentSetUserEmailInput,
  AgentSetUserPhoneInput,
  AgentCreateEmailOTPInput,
  AgentCreateOTPInput,
  AgentResendOTPInput,
  AgentCheckParams,
  AgentCheckUserOutput,
  AgentSetProfileOutput,
  AgentSetProfileInput,
  AgentSetUsernameInput,
} from '../api'
import { agentAuthDetailsOptions } from './agent-auth.options'
import { EAgentAuthQueryKey } from './agent-auth.types'
import type { IMutationArgs } from '../types'

export const useAgentLoginByEmail = () => {
  return useMutation({
    mutationFn: (data: AgentLoginByEmailInput) =>
      agentAuthService.agentLoginByEmail(data),
  })
}

export const useAgentLoginByPhone = () => {
  return useMutation({
    mutationFn: (data: AgentLoginByPhoneInput) =>
      agentAuthService.agentLoginByPhone(data),
  })
}

export const useAgentRegisterByEmail = () => {
  return useMutation({
    mutationFn: (data: AgentRegisterByEmailInput) =>
      agentAuthService.agentRegisterByEmail(data),
  })
}

export const useAgentRegisterByPhone = () => {
  return useMutation({
    mutationFn: (data: AgentRegisterByPhoneInput) =>
      agentAuthService.agentRegisterByPhone(data),
  })
}

export const useAgentRefresh = () => {
  return useMutation({
    mutationFn: (data: AgentRefreshTokenInput) =>
      agentAuthService.agentRefresh(data),
  })
}

export const useAgentSetUserEmail = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args
  return useMutation({
    mutationFn: (data: AgentSetUserEmailInput) =>
      agentAuthService.agentSetUserEmail(data),
    onSuccess: (data) => {
      if (invalidate)
        queryClient.invalidateQueries({
          queryKey: [EAgentAuthQueryKey.AGENT_AUTH_DETAILS],
        })
      return data
    },
  })
}

export const useAgentSetUserPhone = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args
  return useMutation({
    mutationFn: (data: AgentSetUserPhoneInput) =>
      agentAuthService.agentSetUserPhone(data),
    onSuccess: (data) => {
      if (invalidate)
        queryClient.invalidateQueries({
          queryKey: [EAgentAuthQueryKey.AGENT_AUTH_DETAILS],
        })
      return data
    },
  })
}

export const usePrefetchAgentAuthDetails = () => {
  return useSuspenseQuery(queryOptions(agentAuthDetailsOptions()))
}

export const useAgentAuthDetails = () => {
  return useQuery(agentAuthDetailsOptions())
}

export const useAgentLogout = () => {
  const queryClient = useQueryClient()
  const clearCache = () => {
    queryClient.clear()
  }
  return { clearCache }
}

export const useAgentEmailOtp = () => {
  return useMutation({
    mutationFn: (data: AgentCreateEmailOTPInput) =>
      agentAuthService.agentCreateEmailOtp(data),
  })
}

export const useAgentPhoneOtp = () => {
  return useMutation({
    mutationFn: (data: AgentCreateOTPInput) =>
      agentAuthService.agentCreatePhoneOtp(data),
  })
}

export function useAgentResendOtp() {
  return useMutation({
    mutationFn: (data: AgentResendOTPInput) =>
      agentAuthService.agentResendOtp(data),
  })
}

export function useAgentCheck(): UseMutationResult<
  AgentCheckUserOutput,
  Error,
  AgentCheckParams,
  unknown
> {
  return useMutation({
    mutationFn: (data: AgentCheckParams) => agentAuthService.agentCheck(data),
  })
}

export function useAgentSetProfile(
  args: IMutationArgs,
): UseMutationResult<AgentSetProfileOutput, Error, AgentSetProfileInput> {
  const { invalidate } = args
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: AgentSetProfileInput) =>
      agentAuthService.agentSetProfile(data),
    onSuccess: (data) => {
      if (invalidate)
        queryClient.invalidateQueries({
          queryKey: [EAgentAuthQueryKey.AGENT_AUTH_DETAILS],
        })
      return data
    },
  })
}

export const useAgentUploadProfilePicture = () => {
  return useMutation({
    mutationFn: (formData: FormData) =>
      agentAuthService.agentUploadProfilePicture(formData),
  })
}

export const useAgentUploadCoverPicture = (args: IMutationArgs) => {
  const { invalidate } = args
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (formData: FormData) =>
      agentAuthService.agentUploadCoverPicture(formData),
    onSuccess: (data) => {
      if (invalidate)
        queryClient.invalidateQueries({
          queryKey: [EAgentAuthQueryKey.AGENT_AUTH_DETAILS],
        })
      return data
    },
  })
}
export const useAgentSetUsername = (args: IMutationArgs) => {
  const { invalidate } = args
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: AgentSetUsernameInput) =>
      agentAuthService.agentSetUsername(data),
    onSuccess: (data) => {
      if (invalidate)
        queryClient.invalidateQueries({
          queryKey: [EAgentAuthQueryKey.AGENT_AUTH_DETAILS],
        })
      return data
    },
  })
}
